import { Minus, Plus } from "lucide-react";
import { useEffect, useId, useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { C } from "./styles";

/** 胶囊标签 */
export function Pill({ children, tint = C.chip, ink = C.ink, size = 13 }: { children: ReactNode; tint?: string; ink?: string; size?: number }) {
  return (
    <span className="kp-pill" style={{ background: tint, color: ink, fontSize: size }}>
      {children}
    </span>
  );
}

/** 小胶囊选项（深色 = 选中） */
export function ChoiceChip({
  children,
  selected,
  onClick,
  height = 32,
  unselectedBg = C.bg,
  disabled,
}: {
  children: ReactNode;
  selected: boolean;
  onClick: () => void;
  height?: number;
  unselectedBg?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={`kp-chip ${selected ? "is-on" : ""}`}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      style={{ height, background: selected ? undefined : unselectedBg }}
    >
      {children}
    </button>
  );
}

/** 虚线边框胶囊（"+ 添加"） */
export function DashedChip({ children, onClick, height = 32 }: { children: ReactNode; onClick?: () => void; height?: number }) {
  return (
    <button type="button" className="kp-chip kp-chip--dashed" onClick={onClick} style={{ height }}>
      {children}
    </button>
  );
}

/** 白底胶囊里的分段选择（日/周/月、内容库/已布置） */
export function PillSegment<T extends string | number>({
  items,
  value,
  onChange,
  height = 32,
  stretch,
  background = C.surface,
}: {
  items: readonly (readonly [T, string])[];
  value: T;
  onChange: (v: T) => void;
  height?: number;
  stretch?: boolean;
  background?: string;
}) {
  return (
    <div className={`kp-segment ${stretch ? "is-stretch" : ""}`} role="tablist" style={{ background }}>
      {items.map(([k, label]) => (
        <button key={String(k)} type="button" role="tab" aria-selected={k === value} className={k === value ? "is-on" : ""} style={{ height }} onClick={() => onChange(k)}>
          {label}
        </button>
      ))}
    </div>
  );
}

/** 开关：50×30，开为绿色。传 label 时整行可点 */
export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean }) {
  const track = (
    <span className={`kp-switch ${checked ? "is-on" : ""}`} aria-hidden>
      <span />
    </span>
  );
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} className={label != null ? "kp-switch-row" : "kp-switch-btn"} onClick={() => onChange(!checked)}>
      {label != null && <span className="kp-switch-label">{label}</span>}
      {track}
    </button>
  );
}

/** 数字步进（− 10 +）：中间的数字也可以直接输入（输入中在范围内就生效，离开或回车时按范围修正） */
export function NumberStepper({ value, onChange, min = 0, max = 999, step = 1 }: { value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number }) {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);
  const digits = String(max).length;
  const set = (v: number) => {
    const n = Math.min(max, Math.max(min, v));
    onChange(n);
    setText(String(n));
  };
  return (
    <div className="kp-stepper">
      <button type="button" aria-label="减少" disabled={value <= min} onClick={() => set(value - step)}>
        <Minus size={16} strokeWidth={3} />
      </button>
      <input
        className="kp-stepper-input kp-num"
        inputMode="numeric"
        aria-label="数值"
        value={text}
        style={{ width: `${Math.max(2, digits) + 1.6}ch` }}
        onFocus={(e) => {
          setFocused(true);
          e.target.select();
        }}
        onChange={(e) => {
          const d = e.target.value.replace(/\D/g, "").slice(0, digits);
          setText(d);
          const n = Number(d);
          if (d && n >= min && n <= max && n !== value) onChange(n);
        }}
        onBlur={() => {
          setFocused(false);
          set(text ? Number(text) : value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
      <button type="button" aria-label="增加" disabled={value >= max} onClick={() => set(value + step)}>
        <Plus size={16} strokeWidth={3} />
      </button>
    </div>
  );
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  value: string;
  onChange: (v: string) => void;
  /** 输入框上方的小标题 */
  label?: string;
}

/** 输入框（米色底，48 高） */
export function TextField({ value, onChange, label, className, id, ...rest }: TextFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const input = (
    <input
      id={inputId}
      className={`kp-field ${className ?? ""}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoCapitalize="off"
      autoCorrect="off"
      spellCheck={false}
      {...rest}
    />
  );
  if (!label) return input;
  return (
    <label className="kp-field-wrap" htmlFor={inputId}>
      <span className="kp-field-label">{label}</span>
      {input}
    </label>
  );
}

/** 同 TextField（与 iOS 组件同名） */
export const FieldBox = TextField;

/** 多行输入框 */
export function TextArea({
  value,
  onChange,
  label,
  className,
  ...rest
}: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange" | "value"> & { value: string; onChange: (v: string) => void; label?: string }) {
  const area = <textarea className={`kp-field kp-field--area ${className ?? ""}`} value={value} onChange={(e) => onChange(e.target.value)} {...rest} />;
  if (!label) return area;
  return (
    <label className="kp-field-wrap">
      <span className="kp-field-label">{label}</span>
      {area}
    </label>
  );
}
